import React from 'react';
import { Card, Row, Col, Skeleton } from 'antd';
import {
  DollarOutlined,
  FundOutlined,
  FileTextOutlined,
  TeamOutlined,
  BankOutlined,
  WalletOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';

const KPIMetricsRow = ({ data, loading, onNavigate }) => {
  const formatCurrency = (value) => {
    if (!value || value === 0) return '₹0';
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value?.toLocaleString('en-IN') || 0}`;
  };

  const renderGrowthIndicator = (growth) => {
    if (growth === undefined || growth === null) return null;
    const isPositive = growth >= 0;
    return (
      <span style={{
        fontSize: '12px',
        color: isPositive ? '#52c41a' : '#ff4d4f',
        marginLeft: '4px'
      }}>
        {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
        {Math.abs(growth)}%
      </span>
    );
  };

  // Debug: Log data to console
  console.log('KPIMetricsRow data:', data);

  const metrics = [
    {
      key: 'revenue',
      title: 'Revenue',
      value: data?.revenue?.total || 0,
      growth: data?.revenue?.growth,
      formatter: formatCurrency,
      icon: <DollarOutlined />,
      color: '#52c41a',
      path: '/reports/sales',
      bgColor: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
    },
    {
      key: 'expenses',
      title: 'Expenses',
      value: data?.expenses_total || data?.expenses || 0,
      growth: data?.expenses_growth,
      formatter: formatCurrency,
      icon: <WalletOutlined />,
      color: '#ff4d4f',
      path: '/expenses',
      bgColor: 'linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%)'
    },
    {
      key: 'pipeline',
      title: 'Pipeline',
      value: data?.pipeline?.total || 0,
      growth: data?.pipeline?.growth,
      formatter: formatCurrency,
      icon: <FundOutlined />,
      color: '#1890ff',
      path: '/deals',
      bgColor: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
    },
    {
      key: 'invoices',
      title: 'Invoices',
      value: data?.invoices_total || data?.invoices || 0,
      growth: data?.invoices_growth,
      formatter: (v) => v || 0,
      icon: <FileTextOutlined />,
      color: '#722ed1',
      path: '/invoices',
      bgColor: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)'
    },
    {
      key: 'contacts',
      title: 'Contacts',
      value: data?.contacts_total || data?.contacts || 0,
      growth: data?.contacts_growth,
      formatter: (v) => v || 0,
      icon: <TeamOutlined />,
      color: '#13c2c2',
      path: '/contacts',
      bgColor: 'linear-gradient(135deg, #e6fffb 0%, #b5f5ec 100%)'
    },
    {
      key: 'accounts',
      title: 'Accounts',
      value: data?.accounts_total || data?.accounts || 0,
      growth: data?.accounts_growth,
      formatter: (v) => v || 0,
      icon: <BankOutlined />,
      color: '#fa8c16',
      path: '/accounts',
      bgColor: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)'
    }
  ];

  if (loading) {
    return (
      <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Col xs={12} sm={8} md={6} lg={4} key={i}>
            <Card size="small" style={{ height: '100px' }}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
      {metrics.map((metric) => (
        <Col xs={12} sm={8} md={6} lg={4} key={metric.key}>
          <Card
            size="small"
            className="cursor-pointer"
            onClick={() => onNavigate(metric.path)}
            style={{
              background: metric.bgColor,
              border: 'none',
              height: '100%'
            }}
            hoverable
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{
                color: metric.color,
                fontSize: '16px',
                marginRight: '8px'
              }}>
                {metric.icon}
              </span>
              <span style={{ fontSize: '13px', color: '#595959' }}>
                {metric.title}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <span style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#262626'
              }}>
                {metric.formatter(metric.value)}
              </span>
              {renderGrowthIndicator(metric.growth)}
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default KPIMetricsRow;
